import React from 'react';
import Image from 'next/image';
import Head from 'next/head';
import { ListsyncFooter } from '@/components/listsync/Footer';
import Video from '@/components/video';
import { LISTSYNC_LOGIN_URL } from '@/config-global';

export default function ListSyncLanding() {
  const [activeStep, setActiveStep] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(false);
  const [isTablet, setIsTablet] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  // Detect screen size
  React.useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const steps = [
    {
      title: 'Download & Install XunJu',
      description: 'Get the XunJu app to start managing your listings.',
      image: '/listsync/xunju.png'
    },
    {
      title: 'Connect Accounts',
      description: 'Link your Listsync and SpareRoom accounts securely.',
      image: '/listsync/xunju-1.png'
    },
    {
      title: 'Sync with COHO',
      description: 'Enable the connection with COHO Property Management.',
      image: '/listsync/coho.png'
    }
  ];

  const [selectedPlan, setSelectedPlan] = React.useState<'starter' | 'growth'>(
    'starter'
  );

  const navLinkStyle: React.CSSProperties = {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: 1,
    letterSpacing: 0,
    textTransform: 'capitalize',
    color: '#1a1a1a',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    cursor: 'pointer'
  };

  const loginButtonStyle = (compact: boolean): React.CSSProperties => ({
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 600,
    fontSize: compact ? '15px' : '16px',
    lineHeight: 1,
    letterSpacing: 0,
    color: '#ffffff',
    textDecoration: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    padding: compact ? '10px 18px' : '10px 22px',
    borderRadius: '999px',
    background: 'linear-gradient(135deg, #4F46E5 0%, #4338ca 100%)',
    boxShadow: '0 8px 20px -6px rgba(79, 70, 229, 0.45)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  });

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetId: string
  ) => {
    e.preventDefault();
    if (targetId === '#') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const element = document.querySelector(targetId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <Head>
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@600&display=swap"
          rel="stylesheet"
        />
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .listsync-nav a:hover { color: #4F46E5 !important; }
              html { scroll-behavior: smooth; }
            `
          }}
        />
      </Head>
      <div
        style={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          backgroundColor: '#ffffff',
          minHeight: '100vh'
        }}
      >
        {/* Header */}
        <header
          style={{
            padding: isMobile ? '16px 20px' : '24px 60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'transparent',
            position: 'relative',
            zIndex: 10,
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%',
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Image
              src="/listsync/listsync-logo.svg"
              alt="Listsync Logo"
              width={isMobile ? 120 : 160}
              height={isMobile ? 41 : 55}
              style={{ height: 'auto' }}
            />
          </div>
          {isMobile ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginLeft: 'auto'
                }}
              >
                <a
                  href={LISTSYNC_LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={loginButtonStyle(true)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow =
                      '0 12px 24px -6px rgba(79, 70, 229, 0.55)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 8px 20px -6px rgba(79, 70, 229, 0.45)';
                  }}
                >
                  Log in
                </a>
                <button
                  onClick={() => setIsMenuOpen(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px'
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 12H21"
                      stroke="#1a1a1a"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 6H21"
                      stroke="#1a1a1a"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 18H21"
                      stroke="#1a1a1a"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              {/* Mobile Menu Overlay */}
              {isMenuOpen && (
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'white',
                    zIndex: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '20px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginBottom: '40px'
                    }}
                  >
                    <button
                      onClick={() => setIsMenuOpen(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px'
                      }}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18"
                          stroke="#1a1a1a"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 6L18 18"
                          stroke="#1a1a1a"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <nav
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '32px'
                    }}
                  >
                    <a
                      href="#"
                      onClick={(e) => {
                        handleNavClick(e, '#');
                        setIsMenuOpen(false);
                      }}
                      style={{ ...navLinkStyle, fontSize: '24px' }}
                    >
                      Home
                    </a>
                    <a
                      href="#why"
                      onClick={(e) => {
                        handleNavClick(e, '#why');
                        setIsMenuOpen(false);
                      }}
                      style={{ ...navLinkStyle, fontSize: '24px' }}
                    >
                      Why?
                    </a>
                    <a
                      href="#features"
                      onClick={(e) => {
                        handleNavClick(e, '#features');
                        setIsMenuOpen(false);
                      }}
                      style={{ ...navLinkStyle, fontSize: '24px' }}
                    >
                      Features
                    </a>
                    <a
                      href="#how"
                      onClick={(e) => {
                        handleNavClick(e, '#how');
                        setIsMenuOpen(false);
                      }}
                      style={{ ...navLinkStyle, fontSize: '24px' }}
                    >
                      How To Start
                    </a>
                    <a
                      href="#pricing"
                      onClick={(e) => {
                        handleNavClick(e, '#pricing');
                        setIsMenuOpen(false);
                      }}
                      style={{ ...navLinkStyle, fontSize: '24px' }}
                    >
                      Pricing
                    </a>
                    <a
                      href="#who"
                      onClick={(e) => {
                        handleNavClick(e, '#who');
                        setIsMenuOpen(false);
                      }}
                      style={{ ...navLinkStyle, fontSize: '24px' }}
                    >
                      Who&apos;s It For
                    </a>
                    <a
                      href={LISTSYNC_LOGIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMenuOpen(false)}
                      style={{
                        ...loginButtonStyle(false),
                        marginTop: '8px',
                        fontSize: '18px',
                        padding: '14px 28px',
                        alignSelf: 'flex-start'
                      }}
                    >
                      Log in
                    </a>
                  </nav>
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '28px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end'
              }}
            >
              <nav
                className="listsync-nav"
                style={{ display: 'flex', gap: '40px', alignItems: 'center' }}
              >
                <a
                  href="#"
                  onClick={(e) => handleNavClick(e, '#')}
                  style={navLinkStyle}
                >
                  Home
                </a>
                <a
                  href="#why"
                  onClick={(e) => handleNavClick(e, '#why')}
                  style={navLinkStyle}
                >
                  Why?
                </a>
                <a
                  href="#features"
                  onClick={(e) => handleNavClick(e, '#features')}
                  style={navLinkStyle}
                >
                  Features
                </a>
                <a
                  href="#how"
                  onClick={(e) => handleNavClick(e, '#how')}
                  style={navLinkStyle}
                >
                  How To Start
                </a>
                <a
                  href="#pricing"
                  onClick={(e) => handleNavClick(e, '#pricing')}
                  style={navLinkStyle}
                >
                  Pricing
                </a>
                <a
                  href="#who"
                  onClick={(e) => handleNavClick(e, '#who')}
                  style={navLinkStyle}
                >
                  Who&apos;s It For
                </a>
              </nav>
              <a
                href={LISTSYNC_LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={loginButtonStyle(false)}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow =
                    '0 12px 24px -6px rgba(79, 70, 229, 0.55)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 8px 20px -6px rgba(79, 70, 229, 0.45)';
                }}
              >
                Log in
              </a>
            </div>
          )}
        </header>

        {/* Hero Section */}
        <section
          style={{
            padding: isMobile ? '40px 20px 60px' : '60px 40px 100px',
            backgroundColor: '#ffffff',
            backgroundImage: 'url(/listsync/background.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            minHeight: isMobile ? 'auto' : '700px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <div
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? '40px' : '80px',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <div style={{ order: isMobile ? 2 : 1 }}>
              <h1
                style={{
                  fontSize: isMobile ? '32px' : '56px',
                  fontWeight: '800',
                  color: '#4F46E5',
                  lineHeight: '1.1',
                  marginBottom: isMobile ? '16px' : '24px',
                  letterSpacing: '-0.02em',
                  textAlign: isMobile ? 'center' : 'left'
                }}
              >
                Auto-Sync Listings to SpareRoom
              </h1>
              <p
                style={{
                  fontSize: isMobile ? '16px' : '20px',
                  color: '#4b5563',
                  lineHeight: '1.6',
                  marginBottom: isMobile ? '24px' : '40px',
                  maxWidth: '540px',
                  marginLeft: isMobile ? 'auto' : '0',
                  marginRight: isMobile ? 'auto' : '0',
                  textAlign: isMobile ? 'center' : 'left'
                }}
              >
                Listsync keeps your rental listings aligned between COHO
                Property Management Software and SpareRoom
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: isMobile ? 'center' : 'flex-start'
                }}
              >
                <button
                  style={{
                    background:
                      'linear-gradient(135deg, #4F46E5 0%, #4338ca 100%)',
                    color: 'white',
                    padding: isMobile ? '14px 32px' : '16px 40px',
                    borderRadius: '50px',
                    border: 'none',
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.4)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 20px 25px -5px rgba(79, 70, 229, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 10px 25px -5px rgba(79, 70, 229, 0.4)';
                  }}
                  onClick={() =>
                    (window.location.href =
                      'https://spareroom-automations.listsync.co.uk/')
                  }
                >
                  Start Syncing Now
                </button>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                order: isMobile ? 1 : 2
              }}
            >
              <div
                style={{
                  position: 'relative',
                  zIndex: 2,
                  animation: 'float 6s ease-in-out infinite'
                }}
              >
                <Image
                  src="/listsync/listsync-hero.png"
                  alt="Listsync Hero"
                  width={isMobile ? 300 : 600}
                  height={isMobile ? 250 : 500}
                  style={{
                    width: '100%',
                    height: 'auto',
                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.1))'
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Does This Tool Exist */}
        <section
          id="why"
          style={{
            backgroundColor: 'white',
            padding: isMobile ? '60px 20px' : '100px 40px'
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobile ? '28px' : '42px',
                fontWeight: '700',
                textAlign: 'center',
                marginBottom: isMobile ? '40px' : '80px',
                color: '#1a1a1a',
                letterSpacing: '-0.02em'
              }}
            >
              Select your cookie preferences
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr',
                gap: isMobile ? '12px' : '40px',
                position: 'relative',
                alignItems: 'stretch'
              }}
            >
              {/* VS Badge */}
              {!isMobile && (
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 5,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    fontSize: '24px',
                    fontWeight: '800',
                    color: '#4F46E5',
                    border: '4px solid white'
                  }}
                >
                  VS
                </div>
              )}

              {/* We've Joined */}
              <div
                style={{
                  backgroundColor: '#f8fafc',
                  borderRadius: isMobile ? '16px' : '24px 0 0 24px',
                  padding: isMobile ? '20px 12px' : '60px 40px',
                  border: '1px solid #e2e8f0',
                  borderRight: isMobile ? '1px solid #e2e8f0' : 'none',
                  clipPath: isMobile
                    ? 'none'
                    : 'polygon(0 0, 92% 0, 100% 100%, 0% 100%)'
                }}
              >
                <h3
                  style={{
                    fontSize: isMobile ? '16px' : '24px',
                    fontWeight: '600',
                    marginBottom: isMobile ? '16px' : '32px',
                    color: '#1a1a1a',
                    textAlign: 'center'
                  }}
                >
                  Without Listsync
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Manual data entry',
                    'Repetitive admin tasks',
                    'Out-of-date listings',
                    'Lost tenant leads'
                  ].map((item, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: isMobile ? '8px' : '16px',
                        marginBottom: isMobile ? '12px' : '20px',
                        color: '#64748b',
                        fontSize: isMobile ? '13px' : '18px',
                        lineHeight: isMobile ? '1.4' : '1.5'
                      }}
                    >
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fee2e2',
                          borderRadius: '50%',
                          flexShrink: 0
                        }}
                      >
                        <Image
                          src="/listsync/close.svg"
                          alt="X"
                          width={12}
                          height={12}
                          style={{
                            filter:
                              'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)'
                          }}
                        />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* With Listsync */}
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #4F46E5 0%, #4338ca 100%)',
                  borderRadius: isMobile ? '16px' : '0 24px 24px 0',
                  padding: isMobile ? '20px 12px' : '60px 40px 60px 80px',
                  color: 'white',
                  boxShadow: '0 20px 40px -10px rgba(79, 70, 229, 0.3)',
                  position: 'relative',
                  zIndex: 1,
                  clipPath: isMobile
                    ? 'none'
                    : 'polygon(0 0, 100% 0, 100% 100%, 8% 100%)',
                  marginTop: isMobile ? '0' : '0'
                }}
              >
                <h3
                  style={{
                    fontSize: isMobile ? '16px' : '24px',
                    fontWeight: '600',
                    marginBottom: isMobile ? '16px' : '32px',
                    textAlign: 'center'
                  }}
                >
                  With Listsync
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Automated syncing',
                    'Zero repetitive work',
                    'Real-time updates',
                    'Maximized tenant leads'
                  ].map((item, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: isMobile ? '8px' : '16px',
                        marginBottom: isMobile ? '12px' : '20px',
                        fontSize: isMobile ? '13px' : '18px',
                        lineHeight: isMobile ? '1.4' : '1.5'
                      }}
                    >
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          borderRadius: '50%',
                          flexShrink: 0
                        }}
                      >
                        <Image
                          src="/listsync/check.svg"
                          alt="Check"
                          width={14}
                          height={14}
                          style={{ filter: 'brightness(0) invert(1)' }}
                        />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section
          id="features"
          style={{
            backgroundColor: 'white',
            padding: isMobile ? '60px 20px' : '100px 40px'
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobile ? '28px' : '42px',
                fontWeight: '700',
                textAlign: 'center',
                marginBottom: isMobile ? '40px' : '80px',
                color: '#1a1a1a',
                letterSpacing: '-0.02em'
              }}
            >
              Benefits
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile
                  ? '1fr'
                  : isTablet
                  ? 'repeat(2, 1fr)'
                  : 'repeat(5, 1fr)',
                gap: isMobile ? '32px' : '40px'
              }}
            >
              {[
                {
                  icon: '/listsync/save-time.svg',
                  title: 'Save hours every week on admin',
                  desc: ''
                },
                {
                  icon: '/listsync/convert.svg',
                  title: 'Build faster lead funnels by updating instantly',
                  desc: ''
                },
                {
                  icon: '/listsync/reduce.svg',
                  title: 'Reduce costly listing mistakes',
                  desc: ''
                },
                {
                  icon: '/listsync/improve.svg',
                  title: 'Improve tenant experience with accurate data',
                  desc: ''
                },
                {
                  icon: '/listsync/scale.svg',
                  title: 'Scale room-rental portfolios effortlessly',
                  desc: ''
                }
              ].map((benefit, i) => (
                <div
                  key={i}
                  style={{
                    textAlign: isMobile ? 'left' : 'center',
                    transition: 'transform 0.2s ease',
                    display: isMobile ? 'flex' : 'block',
                    alignItems: 'center',
                    gap: isMobile ? '24px' : '0'
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.transform = 'translateY(-5px)')
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.transform = 'translateY(0)')
                  }
                >
                  <div
                    style={{
                      width: isMobile ? 'auto' : '100px',
                      height: isMobile ? 'auto' : '100px',
                      margin: isMobile ? '0' : '0 auto 24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isMobile ? 'transparent' : '#f8fafc',
                      borderRadius: isMobile ? '0' : '24px',
                      padding: isMobile ? '0' : '20px',
                      flexShrink: 0
                    }}
                  >
                    <Image
                      src={benefit.icon}
                      alt={benefit.title}
                      width={isMobile ? 64 : 60}
                      height={isMobile ? 64 : 60}
                      style={{
                        width: isMobile ? '64px' : '100%',
                        height: 'auto'
                      }}
                    />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: '600',
                        marginBottom: isMobile ? '4px' : '8px',
                        color: '#1a1a1a',
                        lineHeight: '1.4'
                      }}
                    >
                      {benefit.title}
                    </h3>
                    {benefit.desc && (
                      <p
                        style={{
                          fontSize: isMobile ? '12px' : '14px',
                          color: '#666',
                          lineHeight: '1.5'
                        }}
                      >
                        {benefit.desc}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What is Listsync */}
        <section
          id="what"
          style={{
            backgroundColor: 'white',
            padding: isMobile ? '60px 20px' : '100px 40px',
            background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobile ? '28px' : '42px',
                fontWeight: '700',
                textAlign: 'center',
                marginBottom: isMobile ? '16px' : '24px',
                color: '#1a1a1a',
                letterSpacing: '-0.02em'
              }}
            >
              What is Listsync?
            </h2>
            <p
              style={{
                fontSize: isMobile ? '16px' : '18px',
                color: '#64748b',
                textAlign: 'center',
                maxWidth: '700px',
                margin: `0 auto ${isMobile ? '40px' : '80px'}`,
                lineHeight: '1.6',
                padding: isMobile ? '0 20px' : '0'
              }}
            >
              Listsync is a fully automated data-synchronisation tool built
              specifically for UK HMO letting agencies. It connects your
              property management software and CRMs directly to the room-rental
              portal, SpareRoom.
            </p>

            {/* Video Banner */}
            <div
              style={{
                maxWidth: '1200px',
                margin: `0 auto ${isMobile ? '60px' : '100px'}`,
                padding: isMobile ? '40px 24px' : '60px 100px',
                background: '#f4f6fc',
                borderRadius: '40px',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: isMobile ? '40px' : '100px'
              }}
            >
              <div
                style={{
                  flex: 1.2,
                  textAlign: isMobile ? 'center' : 'left'
                }}
              >
                <h3
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: isMobile ? '24px' : '40px',
                    fontWeight: '700',
                    color: '#2d2d2d',
                    marginBottom: isMobile ? '24px' : '40px',
                    lineHeight: '1.2',
                    letterSpacing: '-0.02em'
                  }}
                >
                  Discover why ListSync alone is all you need among countless
                  software options.
                </h3>
                <Video
                  url="https://listsync.s3.eu-west-2.amazonaws.com/site/Lark20260318-062755.mp4"
                  target={
                    <button
                      style={{
                        background: '#4477ff',
                        color: 'white',
                        padding: isMobile ? '14px 40px' : '16px 60px',
                        borderRadius: '50px',
                        border: 'none',
                        fontSize: isMobile ? '16px' : '20px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        boxShadow: '0 12px 24px rgba(68, 119, 255, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow =
                          '0 15px 30px rgba(68, 119, 255, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow =
                          '0 12px 24px rgba(68, 119, 255, 0.3)';
                      }}
                    >
                      Watch Now
                    </button>
                  }
                />
              </div>
              <div
                style={{
                  flex: 1,
                  position: 'relative',
                  width: '100%'
                }}
              >
                <Video
                  url="https://listsync.s3.eu-west-2.amazonaws.com/site/Lark20260318-062755.mp4"
                  target={
                    <div
                      style={{
                        cursor: 'pointer',
                        position: 'relative',
                        borderRadius: '24px',
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                        transition: 'transform 0.3s ease'
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.transform = 'scale(1.02)')
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.transform = 'scale(1)')
                      }
                    >
                      <Image
                        src="/listsync/video.png"
                        alt="Watch Video"
                        width={600}
                        height={340}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '80px',
                          height: '80px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid rgba(255, 255, 255, 0.5)'
                        }}
                      >
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="white"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  }
                />
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: isMobile ? '40px' : '60px'
              }}
            >
              {isMobile ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '320px'
                  }}
                >
                  {/* Step 1 */}
                  <Image
                    src="/listsync/coho_1.png"
                    alt="COHO"
                    width={280}
                    height={200}
                    style={{ width: 'auto', maxWidth: '100%', height: 'auto' }}
                  />

                  {/* Arrow 1 */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1a1a1a'
                      }}
                    >
                      Sync
                    </span>
                    <Image
                      src="/listsync/union.png"
                      alt="Arrow"
                      width={12}
                      height={24}
                    />
                  </div>

                  {/* Step 2 */}
                  <Image
                    src="/listsync/coho_2.png"
                    alt="Listsync"
                    width={280}
                    height={200}
                    style={{ width: 'auto', maxWidth: '100%', height: 'auto' }}
                  />

                  {/* Arrow 2 */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1a1a1a'
                      }}
                    >
                      Sync
                    </span>
                    <Image
                      src="/listsync/union.png"
                      alt="Arrow"
                      width={12}
                      height={24}
                    />
                  </div>

                  {/* Step 3 */}
                  <Image
                    src="/listsync/coho_3.png"
                    alt="SpareRoom"
                    width={280}
                    height={200}
                    style={{ width: 'auto', maxWidth: '100%', height: 'auto' }}
                  />
                </div>
              ) : (
                <Image
                  src="/listsync/connect.png"
                  alt="How Listsync Works"
                  width={900}
                  height={340}
                  style={{
                    width: '100%',
                    maxWidth: '900px',
                    height: 'auto',
                    marginBottom: '40px'
                  }}
                />
              )}
              <p
                style={{
                  fontSize: isMobile ? '15px' : '20px',
                  color: isMobile ? '#4b5563' : '#1a1a1a',
                  fontWeight: '600',
                  textAlign: 'center',
                  letterSpacing: '-0.01em',
                  padding: isMobile ? '0 40px' : '0',
                  lineHeight: '1.5'
                }}
              >
                The Data Connection Bridge Between COHO And SpareRoom
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button
                style={{
                  background:
                    'linear-gradient(135deg, #4F46E5 0%, #4338ca 100%)',
                  color: 'white',
                  padding: isMobile ? '14px 32px' : '16px 48px',
                  borderRadius: '50px',
                  border: 'none',
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.4)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow =
                    '0 20px 25px -5px rgba(79, 70, 229, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 10px 25px -5px rgba(79, 70, 229, 0.4)';
                }}
                onClick={() =>
                  (window.location.href =
                    'https://spareroom-automations.listsync.co.uk')
                }
              >
                Get Started
              </button>
            </div>
          </div>
        </section>

        {/* How to Start */}
        <section
          id="how"
          style={{
            backgroundColor: 'white',
            padding: isMobile ? '60px 20px' : '100px 40px'
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobile ? '28px' : '42px',
                fontWeight: '700',
                textAlign: 'center',
                marginBottom: isMobile ? '40px' : '80px',
                color: '#1a1a1a',
                letterSpacing: '-0.02em'
              }}
            >
              How to Start
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1.5fr',
                gap: isMobile ? '32px' : '60px',
                alignItems: 'center'
              }}
            >
              {/* Left: Navigation */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px'
                }}
              >
                {steps.map((step, index) => (
                  <div
                    key={index}
                    onClick={() => setActiveStep(index)}
                    style={{
                      padding: isMobile ? '20px' : '32px',
                      borderRadius: '24px',
                      cursor: 'pointer',
                      backgroundColor:
                        activeStep === index ? '#f8fafc' : 'transparent',
                      border:
                        activeStep === index
                          ? '2px solid #4F46E5'
                          : '2px solid transparent',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      gap: isMobile ? '16px' : '24px',
                      alignItems: 'flex-start'
                    }}
                  >
                    <div
                      style={{
                        width: isMobile ? '32px' : '40px',
                        height: isMobile ? '32px' : '40px',
                        borderRadius: '50%',
                        backgroundColor:
                          activeStep === index ? '#4F46E5' : '#e2e8f0',
                        color: activeStep === index ? 'white' : '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: isMobile ? '16px' : '18px',
                        flexShrink: 0,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {index + 1}
                    </div>
                    <div>
                      <h3
                        style={{
                          fontSize: isMobile ? '18px' : '20px',
                          fontWeight: '700',
                          color: '#1a1a1a',
                          marginBottom: '8px'
                        }}
                      >
                        {step.title}
                      </h3>
                      <p
                        style={{
                          fontSize: isMobile ? '14px' : '16px',
                          color: '#64748b',
                          lineHeight: '1.5',
                          margin: 0
                        }}
                      >
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right: Image */}
              <div
                style={{
                  position: 'relative',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  backgroundColor: '#f1f5f9',
                  minHeight: isMobile ? '200px' : '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: isMobile ? '20px' : '40px'
                }}
              >
                <Image
                  src={steps[activeStep].image}
                  alt={steps[activeStep].title}
                  width={800}
                  height={450}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    transition: 'opacity 0.3s ease'
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section
          id="pricing"
          style={{
            backgroundColor: '#f8fafc',
            padding: isMobile ? '60px 20px' : '100px 40px'
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobile ? '28px' : '42px',
                fontWeight: '700',
                textAlign: 'center',
                marginBottom: isMobile ? '40px' : '80px',
                color: '#1a1a1a',
                letterSpacing: '-0.02em'
              }}
            >
              Pricing & Packages
            </h2>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {/* Main Pricing Card */}
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: isMobile ? '24px' : '32px',
                  padding: isMobile ? '32px 24px' : '50px',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
                  maxWidth: '500px',
                  width: '100%',
                  border: '1px solid #e2e8f0'
                }}
              >
                {/* Plan Selector */}
                <div
                  style={{
                    display: 'flex',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '16px',
                    padding: '6px',
                    marginBottom: isMobile ? '24px' : '40px',
                    position: 'relative'
                  }}
                >
                  <button
                    onClick={() => setSelectedPlan('starter')}
                    style={{
                      flex: 1,
                      padding: isMobile ? '10px' : '12px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor:
                        selectedPlan === 'starter' ? 'white' : 'transparent',
                      color: selectedPlan === 'starter' ? '#1a1a1a' : '#64748b',
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      boxShadow:
                        selectedPlan === 'starter'
                          ? '0 4px 12px rgba(0,0,0,0.05)'
                          : 'none',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? '6px' : '8px'
                    }}
                  >
                    <Image
                      src="/listsync/starter.svg"
                      alt=""
                      width={isMobile ? 20 : 24}
                      height={isMobile ? 20 : 24}
                    />
                    Starter
                  </button>
                  <button
                    onClick={() => setSelectedPlan('growth')}
                    style={{
                      flex: 1,
                      padding: isMobile ? '10px' : '12px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor:
                        selectedPlan === 'growth' ? 'white' : 'transparent',
                      color: selectedPlan === 'growth' ? '#1a1a1a' : '#64748b',
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: selectedPlan === 'growth' ? '700' : '600',
                      cursor: 'pointer',
                      boxShadow:
                        selectedPlan === 'growth'
                          ? '0 4px 12px rgba(0,0,0,0.05)'
                          : 'none',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? '6px' : '8px'
                    }}
                  >
                    <Image
                      src="/listsync/growth.svg"
                      alt=""
                      width={isMobile ? 20 : 24}
                      height={isMobile ? 20 : 24}
                    />
                    Growth
                  </button>
                </div>

                {/* Price Display */}
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: isMobile ? '24px' : '40px'
                  }}
                >
                  <div
                    style={{
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'center'
                    }}
                  >
                    <span
                      style={{
                        fontSize: isMobile ? '48px' : '64px',
                        fontWeight: '800',
                        color: '#1a1a1a',
                        lineHeight: 1,
                        letterSpacing: '-0.03em'
                      }}
                    >
                      £{selectedPlan === 'starter' ? '50' : '100'}
                    </span>
                    <span
                      style={{
                        fontSize: isMobile ? '16px' : '20px',
                        color: '#64748b',
                        marginLeft: '8px',
                        fontWeight: '500'
                      }}
                    >
                      /Month
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#eff6ff',
                      color: '#3b82f6',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: '600'
                    }}
                  >
                    {selectedPlan === 'starter'
                      ? 'Billed yearly as £500/year, save £100'
                      : 'Billed yearly as £1000/year, save £200'}
                  </div>
                </div>

                {/* Listings Count */}
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: isMobile ? '24px' : '32px',
                    padding: isMobile ? '16px' : '20px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? '14px' : '16px',
                      color: '#64748b',
                      marginBottom: '8px',
                      fontWeight: '500'
                    }}
                  >
                    Up to{' '}
                    <span
                      style={{
                        fontSize: isMobile ? '20px' : '24px',
                        fontWeight: '700',
                        color: '#1a1a1a'
                      }}
                    >
                      {selectedPlan === 'starter' ? '20' : '50'}
                    </span>{' '}
                    live marketing listings per month
                  </div>
                </div>

                {/* Features List */}
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: `0 0 ${isMobile ? '24px' : '40px'} 0`
                  }}
                >
                  {[
                    'Basic Property Info',
                    'Amenities & Features',
                    'Room Details',
                    'Pricing & Terms',
                    'Property Description'
                  ].map((feature, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? '12px' : '16px',
                        marginBottom: isMobile ? '16px' : '20px',
                        color: '#334155',
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '500'
                      }}
                    >
                      <div
                        style={{
                          width: isMobile ? '20px' : '24px',
                          height: isMobile ? '20px' : '24px',
                          backgroundColor: '#dcfce7',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Image
                          src="/listsync/check.svg"
                          alt="Check"
                          width={isMobile ? 10 : 12}
                          height={isMobile ? 10 : 12}
                          style={{
                            filter:
                              'brightness(0) saturate(100%) invert(28%) sepia(93%) saturate(1558%) hue-rotate(128deg) brightness(93%) contrast(103%)'
                          }}
                        />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  style={{
                    width: '100%',
                    background:
                      'linear-gradient(135deg, #4F46E5 0%, #4338ca 100%)',
                    color: 'white',
                    padding: isMobile ? '14px' : '18px',
                    borderRadius: '16px',
                    border: 'none',
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    boxShadow: '0 10px 20px -5px rgba(79, 70, 229, 0.3)',
                    transition: 'transform 0.2s ease'
                  }}
                  onClick={() =>
                    (window.location.href =
                      'https://spareroom-automations.listsync.co.uk')
                  }
                >
                  Free Trial
                  <span style={{ fontSize: isMobile ? '18px' : '20px' }}>
                    →
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Who is Listsync For */}
        <section
          id="who"
          style={{
            backgroundColor: 'white',
            padding: isMobile ? '60px 20px' : '100px 40px'
          }}
        >
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobile ? '28px' : '42px',
                fontWeight: '700',
                textAlign: 'center',
                marginBottom: isMobile ? '16px' : '24px',
                color: '#1a1a1a',
                letterSpacing: '-0.02em'
              }}
            >
              Who Is Listsync For?
            </h2>
            <p
              style={{
                fontSize: isMobile ? '16px' : '18px',
                color: '#4b5563',
                textAlign: 'center',
                maxWidth: '800px',
                margin: `0 auto ${isMobile ? '40px' : '80px'}`,
                lineHeight: '1.6',
                padding: isMobile ? '0 20px' : '0'
              }}
            >
              If your business relies on SpareRoom to find tenants and you use
              COHO to manage your room rentals, Listsync saves you the pain of
              keeping them aligned.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? '24px' : '40px'
              }}
            >
              {/* Card 1 */}
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '24px',
                  padding: isMobile ? '24px' : '40px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '20px' : '32px',
                  border: '1px solid #e0e7ff',
                  boxShadow:
                    '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  flexDirection: isMobile ? 'column' : 'row',
                  textAlign: isMobile ? 'center' : 'left'
                }}
                onMouseOver={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow =
                      '0 20px 40px rgba(79, 70, 229, 0.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)';
                  }
                }}
              >
                <Image
                  src="/listsync/hmo.png"
                  alt="HMO letting agents"
                  width={isMobile ? 60 : 80}
                  height={isMobile ? 60 : 80}
                  style={{ flexShrink: 0 }}
                />
                <h3
                  style={{
                    fontSize: isMobile ? '18px' : '20px',
                    fontWeight: '500',
                    color: '#1a1a1a',
                    margin: 0,
                    textAlign: isMobile ? 'center' : 'left',
                    lineHeight: '1.4'
                  }}
                >
                  HMO letting agents
                  <br />
                  managing rooms
                </h3>
              </div>

              {/* Card 2 */}
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '24px',
                  padding: isMobile ? '24px' : '40px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '20px' : '32px',
                  border: '1px solid #e0e7ff',
                  boxShadow:
                    '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  flexDirection: isMobile ? 'column' : 'row',
                  textAlign: isMobile ? 'center' : 'left'
                }}
                onMouseOver={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow =
                      '0 20px 40px rgba(79, 70, 229, 0.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)';
                  }
                }}
              >
                <Image
                  src="/listsync/landlords.png"
                  alt="Landlords"
                  width={isMobile ? 60 : 80}
                  height={isMobile ? 60 : 80}
                  style={{ flexShrink: 0 }}
                />
                <h3
                  style={{
                    fontSize: isMobile ? '18px' : '20px',
                    fontWeight: '500',
                    color: '#1a1a1a',
                    margin: 0,
                    textAlign: isMobile ? 'center' : 'left',
                    lineHeight: '1.4'
                  }}
                >
                  Landlords with HMO
                  <br />
                  portfolios
                </h3>
              </div>
            </div>
          </div>
        </section>

        <ListsyncFooter />
      </div>
    </>
  );
}
